/**
 * Identity & Encounter Recovery T1: the scan identity outcome contract and its copy.
 *
 * The backend returns `identity` on a scan response only when its resolver flag
 * (SCAN_IDENTITY_RESOLVER_V1) is on. When it is absent, every screen behaves exactly as before.
 *
 * - canonical: routed to an existing pricing identity (display unchanged).
 * - resolve: one structured question; the answer is sent after the item is saved.
 * - tracked: the item is saved but not valued (`exact` when the identity is complete).
 * - held: identification pending review.
 * - unavailable: the item could not be identified.
 *
 * User-facing copy never names internal concepts (PIK, canonical, exact, tracked).
 */
import Constants from 'expo-constants';

export interface ResolveCandidate {
  value: string;
  label: string;
}

export interface ResolveQuestion {
  axis: string;
  axis_label: string;
  candidates: ResolveCandidate[];
  remaining_axes: number;
}

export type IdentityOutcomeKind = 'canonical' | 'resolve' | 'tracked' | 'held' | 'unavailable';

export interface IdentityOutcome {
  outcome: IdentityOutcomeKind;
  exact: boolean;
  reason: string;
  family: string | null;
  pricing_identity_key: string | null;
  axes: Record<string, string>;
  question: ResolveQuestion | null;
  /** The revision the open question belongs to; an answer must echo it (stale -> 409). */
  question_revision: string | null;
  resolver_version: string;
}

/** Owner-approved copy (T1). */
export const IDENTITY_COPY = {
  trackedExact: 'Identified · valuation not available yet',
  tracked: 'Item saved · valuation not available yet',
  unavailable: "We couldn't identify this item",
  resolve: (axisLabel: string) => `Add ${axisLabel} to identify this item`,
} as const;

/** Axis labels; mirrors the backend's AXIS_LABELS (scan_identity.py). */
const AXIS_LABELS: Record<string, string> = {
  storage: 'storage',
  ram: 'memory',
  processor: 'chip',
  screen_size: 'screen size',
  connectivity: 'connectivity',
  generation: 'generation',
  model_generation: 'model',
  model_family: 'model',
  carrier_lock: 'lock status',
};

const REASON_PREFIX = 'scan-v1:';

/** The label for an outcome shown in place of a price, or null when a price display applies. */
export function identityOutcomeLabel(identity: IdentityOutcome | null | undefined): string | null {
  if (!identity) return null;
  switch (identity.outcome) {
    case 'resolve':
      return IDENTITY_COPY.resolve(
        identity.question?.axis_label ??
          AXIS_LABELS[identity.reason.split(':').pop() ?? ''] ??
          'details'
      );
    case 'tracked':
      return identity.exact ? IDENTITY_COPY.trackedExact : IDENTITY_COPY.tracked;
    case 'unavailable':
      return IDENTITY_COPY.unavailable;
    default:
      return null;
  }
}

/**
 * S3: true when an identity outcome that shows no market price still carries a credible
 * Cache AI estimate. The backend sends `price_data.source === 'gemini_estimate'` for such an
 * outcome only for a non-Apple item that is tracked or awaiting an answer, with a finite
 * positive estimate; held and unavailable outcomes never carry one.
 */
export function identityShowsEstimate(
  identity: IdentityOutcome | null | undefined,
  priceSource: string | null | undefined
): boolean {
  return (
    !!identity &&
    (identity.outcome === 'tracked' || identity.outcome === 'resolve') &&
    priceSource === 'gemini_estimate'
  );
}

/**
 * The label for a saved item whose identity is unresolved, from its mirrored
 * `user_products.identity_reason`. Null for reasons the resolver did not write.
 */
export function identityReasonLabel(reason: string | null | undefined): string | null {
  if (!reason || !reason.startsWith(REASON_PREFIX)) return null;
  const [, kind, detail] = reason.split(':');
  if (kind === 'resolve') return IDENTITY_COPY.resolve(AXIS_LABELS[detail] ?? 'details');
  if (kind === 'tracked_exact') return IDENTITY_COPY.trackedExact;
  if (kind === 'tracked') return IDENTITY_COPY.tracked;
  if (kind === 'unavailable') return IDENTITY_COPY.unavailable;
  return null;
}

/** True when a saved item has an open identity question it can answer from the item screen. */
export function hasOpenIdentityQuestion(reason: string | null | undefined): boolean {
  return !!reason && reason.startsWith(`${REASON_PREFIX}resolve:`);
}

const BACKEND_API_URL = Constants.expoConfig?.extra?.EXPO_PUBLIC_BACKEND_API_URL;
const API_SECRET = Constants.expoConfig?.extra?.EXPO_PUBLIC_BACKEND_API_SECRET;

interface IdentityResponse {
  success: boolean;
  message: string;
  identity: IdentityOutcome | null;
}

/** A failed identity request; `status` 409 means the question was answered or replaced. */
export class IdentityRequestError extends Error {
  constructor(public readonly status: number) {
    super(`Identity request failed: ${status}`);
    this.name = 'IdentityRequestError';
  }
}

async function identityRequest(
  path: string,
  accessToken: string,
  init: { method: 'GET' | 'POST'; body?: unknown }
): Promise<IdentityResponse> {
  // The web build carries no shared secret: the backend authenticates these routes by the
  // user's token alone (like /run-end-to-end). Native builds still send their secret.
  if (!BACKEND_API_URL) throw new Error('Backend API not configured');
  const response = await fetch(`${BACKEND_API_URL}${path}`, {
    method: init.method,
    headers: {
      'Content-Type': 'application/json',
      ...(API_SECRET ? { 'X-API-Secret': API_SECRET } : {}),
      // Ownership: the backend verifies this Supabase access token and checks the item's owner.
      'X-User-Token': accessToken,
    },
    body: init.body === undefined ? undefined : JSON.stringify(init.body),
  });
  if (!response.ok) {
    throw new IdentityRequestError(response.status);
  }
  return (await response.json()) as IdentityResponse;
}

/** The saved item's identity outcome, including its open question. */
export function getItemIdentity(productId: string, accessToken: string): Promise<IdentityResponse> {
  return identityRequest(`/items/${encodeURIComponent(productId)}/identity`, accessToken, {
    method: 'GET',
  });
}

/**
 * Answer the saved item's open question. `requestKey` makes a retry idempotent: reuse it when
 * retrying the same answer.
 */
export function answerItemIdentity(
  productId: string,
  answer: { axis: string; value: string; requestKey: string; questionRevision: string },
  accessToken: string
): Promise<IdentityResponse> {
  return identityRequest(`/items/${encodeURIComponent(productId)}/identity/answer`, accessToken, {
    method: 'POST',
    body: {
      axis: answer.axis,
      value: answer.value,
      request_key: answer.requestKey,
      question_revision: answer.questionRevision,
    },
  });
}
